import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDateRange } from "@/lib/dateRange";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import { Button } from "@/components/ui/button";
import { Wand2, Sparkles, RefreshCw, Lock } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

export default function Insights() {
  const { range } = useDateRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai/insights", { params: { start: range.start, end: range.end } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const comingSoon = data?.coming_soon === true;
  const bullets = (data?.insights || "").split("\n").filter((l) => l.trim().startsWith("-") || l.trim().startsWith("*"));

  return (
    <div data-testid="insights-page" className="max-w-[1000px] mx-auto">
      <PageHeader
        title="AI Insights"
        subtitle="Claude Sonnet 4.5 reads your numbers and highlights what matters."
        action={
          !comingSoon && (
            <Button data-testid="refresh-insights-btn" onClick={load} disabled={loading} className="rounded-full bg-black text-white hover:bg-black/85">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          )
        }
      />

      <div className="rounded-3xl bg-gradient-to-br from-[#0F1A2E] via-[#0A1424] to-[#000] p-8 text-white mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#007AFF]/20 blur-3xl" />
        <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-[#A18E9F]/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Wand2 className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-semibold text-[15px]">Claude business analyst</div>
              <div className="text-[11px] text-white/60">
                {comingSoon
                  ? "AI-powered insights"
                  : data?.generated_at
                  ? `Generated ${fmtDateTime(data.generated_at)}`
                  : "Analysing…"}
              </div>
            </div>
          </div>

          {/* Coming Soon state — shown when EMERGENT_LLM_KEY is not configured */}
          {comingSoon && (
            <div className="flex flex-col items-center text-center py-8 gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Lock className="w-6 h-6 text-white/70" />
              </div>
              <div>
                <div className="font-display font-semibold text-[18px] mb-1">Coming Soon</div>
                <div className="text-[13px] text-white/60 max-w-sm">
                  Connect an Anthropic API key to unlock AI-generated business analysis powered by Claude Sonnet.
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-2 text-left w-full max-w-sm opacity-40 pointer-events-none select-none">
                {["**Spot** revenue trends before they become problems", "**Optimise** your top-performing product mix", "**Cut** unnecessary expense categories", "**Boost** average order value with targeted actions"].map((b, i) => (
                  <div key={i} className="flex gap-3 text-[13px]">
                    <Sparkles className="w-4 h-4 text-[#5AC8FA] flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading skeleton */}
          {!comingSoon && loading && (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-4 rounded-md bg-white/10 shimmer" style={{ opacity: 1 - i * 0.15 }} />
              ))}
            </div>
          )}

          {/* Insight bullets */}
          {!comingSoon && !loading && bullets.length > 0 && (
            <ul className="space-y-3">
              {bullets.map((b, i) => (
                <li key={i} data-testid={`insight-${i}`} className="flex gap-3 text-[14px] leading-relaxed text-white/90">
                  <Sparkles className="w-4 h-4 text-[#5AC8FA] flex-shrink-0 mt-1" />
                  <span dangerouslySetInnerHTML={{ __html: b.replace(/^[-*]\s*/, "").replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                </li>
              ))}
            </ul>
          )}

          {/* Plain-text fallback */}
          {!comingSoon && !loading && bullets.length === 0 && data?.insights && (
            <p className="text-[14px] whitespace-pre-line text-white/90">{data.insights}</p>
          )}
        </div>
      </div>

      {/* KPI snapshot card — only shown when insights are present */}
      {!comingSoon && data?.kpis && (
        <Card title="Data considered" subtitle="Snapshot fed into the analysis">
          <div className="snapshot-rail grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
            {[
              ["Revenue", `$${data.kpis.revenue}`, `${data.kpis.revenue_change}%`],
              ["Orders", data.kpis.orders, `${data.kpis.orders_change}%`],
              ["AOV", `$${data.kpis.aov}`, `${data.kpis.aov_change}%`],
              ["Net profit", `$${data.kpis.net_profit}`, `${data.kpis.net_profit_change}%`],
            ].map(([l, v, c]) => (
              <div key={l}>
                <div className="text-[10.5px] uppercase tracking-wider text-[#8E8E93]">{l}</div>
                <div className="font-display font-semibold text-[18px] mt-0.5">{v}</div>
                <div className={`text-[11px] ${String(c).startsWith("-") ? "text-[#FF3B30]" : "text-[#34C759]"}`}>{c} vs prev</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
